import { useState } from 'react'
import { useOutletContext } from 'react-router-dom'

import { BlankSlate, Tabs } from '@factorialco/deprecated-design-system'
import { AnimatePresence } from 'framer-motion'
import {
  CurrencySelectorCurrencyFragment,
  ExpenseDetailPageDocument,
  ExpensesRootPageQuery,
  SpendingAlertCreatedDocument,
} from 'generated/resources/graphql'
import { useToggle } from 'usehooks-ts'

import { observer } from 'mobx-react'

import Loading from 'modules/core/components/Loading'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useSubscription } from 'modules/core/hooks/useSubscription'
import i18n from 'modules/core/lib/i18n'

import { SpendingPolicesResultData } from 'modules/expenses/graphql/types'
import { useExpensableDetail } from 'modules/expenses/hooks/useExpensableDetail'
import {
  DuplicateExpenseAlert,
  isDuplicateExpenseAlert,
  useExpenseAlerts,
} from 'modules/expenses/hooks/useExpenseAlerts'
import useLegalEntities from 'modules/expenses/hooks/useLegalEntities'
import {
  getOrganizationForLegalEntity,
  useTaxAgencyHomologations,
} from 'modules/expenses/hooks/useTaxAgencyHomologations'
import { RouteContextParams } from 'modules/expenses/lib/RouteContextParams'
import { isMyExpensesSection } from 'modules/expenses/lib/common'
import {
  isDraftExpensable,
  isExpensableReporter,
  isManualExpensable,
  isOwnExpensable,
  isPendingExpensable,
} from 'modules/expenses/lib/expensables/expensable'
import { trackDuplicateExpense } from 'modules/expenses/lib/expensables/expensableTracking'
import { usePrivateConditionalIntegrationsSyncContext } from 'modules/integrations/hooks/SyncContext'

import Accounting from './_Accounting'
import Alerts from './_Alerts'
import Comments from './_Comments'
import PolicyFlags from './_PolicyFlags'
import ConfirmationModals from './_ConfirmationModals'
import FilesPreviewer from './_FilesPreviewer'
import Form from './_Form'
import Sidepanel from './_Sidepanel'
import { BulkActionsProvider } from './hooks/useBulkActions'
import { useExpensableJournalEntries } from './hooks/useExpensableJournalEntries'
import { FilesPreviewerProvider, parseExpensableFiles } from './hooks/useFilesPreviewer'

interface ExpensableDetailProps {
  expensableId: string
  taxAgencyHomologationEnabled: boolean
  reimburseThroughPayrollEnabled: boolean
  reimburseThroughSepaEnabled?: boolean
  reportOnBehalfEnabled: boolean
  canUseSpendingPolicies?: boolean
  canCreateExpensesForOthers?: boolean
  canOverrideApprovalFlow?: boolean
  canSetExpensableToPaid?: boolean
  canRestartApprovalFlow?: boolean
  canReviewExpensable?: boolean
  canSeeEmployeeLegalEntity: boolean
  canCustomizeSettings: boolean
  accountingTabEnabled: boolean
  projectsManagementEnabled: boolean
  currencies?: CurrencySelectorCurrencyFragment[]
  payrollData?: ExpensesRootPageQuery['payroll']
  compensationsData?: ExpensesRootPageQuery['compensations']
  compensationsEnabled?: boolean
  onClose: () => void
  hasExpensablesF0Forms?: boolean
}

const ExpensableDetail = ({
  expensableId,
  taxAgencyHomologationEnabled,
  reimburseThroughPayrollEnabled,
  reimburseThroughSepaEnabled = false,
  reportOnBehalfEnabled,
  canUseSpendingPolicies = false,
  canCreateExpensesForOthers = false,
  canOverrideApprovalFlow = false,
  canSetExpensableToPaid = false,
  canRestartApprovalFlow = false,
  canReviewExpensable = false,
  canSeeEmployeeLegalEntity,
  canCustomizeSettings,
  accountingTabEnabled,
  projectsManagementEnabled,
  currencies,
  payrollData,
  compensationsData,
  compensationsEnabled,
  onClose,
  hasExpensablesF0Forms,
}: ExpensableDetailProps) => {
  const [hideAlert, toggleAlert] = useToggle(false)
  const [openSidepanel, toggleOpenSidepanel] = useToggle(true)
  const [openFilePreviewer, toggleOpenFilePreviewer] = useToggle(true)
  const [currentTab, setCurrentTab] = useState('detail')

  const { employee: currentEmployee, company } = useCurrentSession()
  const companyId = company?.id

  const { section } = useOutletContext<RouteContextParams>()
  const integrationsSyncDisabled = isMyExpensesSection(section)
  // Integrations framework is disabled for my spending and enabled for spendings
  const syncContext = usePrivateConditionalIntegrationsSyncContext(integrationsSyncDisabled)
  const syncEnabled = syncContext?.syncEnabled ?? false
  const marketplaceIntegrationId = syncContext?.marketplaceIntegration?.id ?? null

  const {
    expensable,
    payroll,
    budget,
    budgetAllocation,
    spendingPoliciesResult: spendingPolicyDataResult,
    loading,
    refetchExpensable,
    refetchSpendingPolicyEvaluate,
  } = useExpensableDetail(
    expensableId,
    reimburseThroughPayrollEnabled,
    canUseSpendingPolicies,
    payrollData,
    compensationsData,
    compensationsEnabled,
    syncEnabled,
    marketplaceIntegrationId
  )

  const {
    data,
    loading: pageDetailsLoading,
    refetch: refetchPageDetails,
  } = useQuery(ExpenseDetailPageDocument, {
    variables: {
      expensableId: String(expensableId),
      companyIdString: companyId?.toString(),
      companyId: companyId,
      reviewerIds: [currentEmployee.id],
    },
  })
  const expensableNode = data?.expenses?.expensablesConnection?.edges[0]?.node
  const spendingAlertsTarget = expensableNode?.spendingAlertsTarget || []
  const spendingAlertsSource = expensableNode?.spendingAlertsSource || []
  const syncableState = expensable?.syncableState?.[0] ?? null

  const approvalsV2Enabled =
    data?.features?.featuresConnection?.nodes.find(
      (f) => f.name === 'dev_expenses_approval_workflows'
    )?.enabled ?? false

  const expensesPolicySettingsEnabled =
    data?.features?.featuresConnection?.nodes.find((f) => f.name === 'dev_expenses_policy_settings')
      ?.enabled ?? false

  const policyComplianceAlerts = spendingAlertsTarget.filter(
    (a) => a.type === 'policy_compliance' || a.type === 'in_policy'
  )
  const hasPolicyComplianceAlerts = policyComplianceAlerts.length > 0
  const aiFlags = policyComplianceAlerts
    .filter((a) => a.type === 'policy_compliance')
    .map((a) => ({
      title: a.metadata?.title ?? '',
      description: a.metadata?.description ?? '',
    }))
  const aiRelevantPolicies = hasPolicyComplianceAlerts
    ? (policyComplianceAlerts[0]?.metadata?.relevantPolicies ?? [])
    : []
  const isRegularExpense = expensable?.type === 'expense'
  const policyFlagsLoading =
    expensesPolicySettingsEnabled &&
    isRegularExpense &&
    (expensableNode?.generatingAlerts ?? false) &&
    !loading

  const allSpendingAlerts = [...spendingAlertsTarget, ...spendingAlertsSource]

  const duplicateAlertsForPolicyFlags: DuplicateExpenseAlert[] = expensesPolicySettingsEnabled
    ? allSpendingAlerts.filter(
        (alert): alert is DuplicateExpenseAlert =>
          alert.type === 'duplicate_expense' && isDuplicateExpenseAlert(alert)
      )
    : []

  const perDiemConflictAlertsForPolicyFlags: DuplicateExpenseAlert[] = expensesPolicySettingsEnabled
    ? allSpendingAlerts.filter(
        (alert): alert is DuplicateExpenseAlert =>
          (alert.type === 'category_conflict' || alert.type === 'per_diem_conflict') &&
          isDuplicateExpenseAlert(alert)
      )
    : []

  const hasMileageRateChangedAlert =
    expensesPolicySettingsEnabled &&
    [...spendingAlertsTarget, ...spendingAlertsSource].some(
      (a) => a.type === 'mileage_rate_changed'
    )

  const mileageFlags =
    hasMileageRateChangedAlert && expensable?.mileage
      ? [
          {
            title: i18n.t('expenses_v2.forms.fields.mileage.alert.manually_changed.title'),
            description: i18n.t(
              'expenses_v2.forms.fields.mileage.alert.manually_changed.comparison',
              {
                calculated_mileage: ((expensable.mileage.calculatedMileage ?? 0) / 100).toString(),
                mileage: ((expensable.mileage.mileage ?? 0) / 100).toString(),
                units: expensable.mileage.units?.toLowerCase() ?? '',
              }
            ),
          },
        ]
      : []

  const allFlags = [...aiFlags, ...mileageFlags]

  useSubscription(SpendingAlertCreatedDocument, {
    onData: () => {
      refetchPageDetails()
    },
  })

  const approvalProcess = expensableNode?.approvalProcess || null

  const timelineEntries = data?.expenses?.approvalsTimelines?.[0]?.entries ?? []

  const spendingAlerts = [...spendingAlertsTarget, ...spendingAlertsSource]

  const [spendingPolicyEvaluationResult, setSpendingPolicyEvaluationResult] = useState<
    SpendingPolicesResultData | undefined
  >(spendingPolicyDataResult)

  const isDraftManual =
    expensable && isManualExpensable(expensable) && isDraftExpensable(expensable)

  const hasVisibleAlertsForNewPolicy = (() => {
    if (expensesPolicySettingsEnabled) return false
    return true
  })()

  const { alertCount, hasDuplicateAlerts } = useExpenseAlerts({
    expensableType: expensable?.type ?? 'expense',
    isDraftManual: isDraftManual ?? false,
    expensableAmount: expensable?.amount,
    reimbursableAmount: expensable?.reimbursableAmount,
    budget,
    budgetAllocation,
    spendingPolicy: spendingPolicyEvaluationResult,
    mileageRoundTrip: expensable?.mileage?.roundTrip ?? false,
    mileageCalculatedMileage: expensable?.mileage?.calculatedMileage ?? 0,
    mileageMileage: expensable?.mileage?.mileage ?? 0,
    spendingAlerts,
    syncableState,
  })

  const { legalEntities } = useLegalEntities()
  // Always use the expense owner's legal entity for signing purposes,
  // not the current employee's (who might be an approver from a different country)
  const legalEntityId = expensable?.owner?.legalEntity?.id

  const signingLegalEntity = legalEntities.find((x) => x.id === legalEntityId)

  const { loading: loadingTaxAgencyHomologation, organizations: taxAgencyOrgs } =
    useTaxAgencyHomologations(signingLegalEntity ? [signingLegalEntity] : [])

  // Find the organization that matches the expense owner's legal entity country
  const organization = getOrganizationForLegalEntity(signingLegalEntity, taxAgencyOrgs)

  const signingOrganization =
    taxAgencyHomologationEnabled && organization?.applies ? organization.id : null

  const handleClose = () => {
    toggleOpenSidepanel()
    onClose()
  }

  const handleCloseClicked = () => {
    toggleOpenFilePreviewer()
  }

  useUnsafeEffect(() => {
    if (expensable && hasDuplicateAlerts) {
      trackDuplicateExpense(expensable)
    }
  }, [expensable, hasDuplicateAlerts])

  useUnsafeEffect(() => {
    setSpendingPolicyEvaluationResult(spendingPolicyDataResult)
  }, [spendingPolicyDataResult])

  const initialFiles = useUnsafeMemo(() => {
    if (expensable) {
      return parseExpensableFiles(expensable)
    }
    return []
  }, [expensable]) //expensable is stable, only changed between graphql refetching

  const customizationFields = data?.expenses?.customizationFields || []
  const signedDocumentsTerms = data?.expenses?.signedDocumentsTermsConnection?.nodes ?? []
  const adeTermsAccepted = signedDocumentsTerms.some(
    (t) => t.organization === 'ade' && t.accepted === true
  )

  const canSeeBudgets = data?.finance._policies.budgetsSeeBudgets.can ?? false
  const canCreateBudgets = data?.finance._policies.budgetsCreateBudgets.can ?? false
  const canCreateSpendingPolicies = data?.finance?._policies?.spendingPolicyCreate?.can ?? false
  const canManageJournalEntries =
    data?.finance?._policies?.journalEntriesManagePurchase?.can ?? false

  const canEditExpensable = expensableNode?._policies?.edit?.can ?? false
  const canDeleteExpensable = expensableNode?._policies?.destroy?.can ?? false

  const isOwnAndDraft =
    expensable && isOwnExpensable(expensable, currentEmployee) && isDraftExpensable(expensable)
  const isReporterAndDraft =
    expensable && isExpensableReporter(expensable, currentEmployee) && isDraftExpensable(expensable)

  const isEditable =
    !expensable ||
    isOwnAndDraft ||
    isReporterAndDraft ||
    (isOwnExpensable(expensable, currentEmployee) && expensable.status === 'changes_requested') ||
    (isExpensableReporter(expensable, currentEmployee) && expensable.status === 'changes_requested') ||
    (canEditExpensable && isPendingExpensable(expensable))

  const anyLoading = loading || loadingTaxAgencyHomologation || pageDetailsLoading

  const { journalEntries, journalDraftEntry } = useExpensableJournalEntries({ expensable })
  const canSeeJournalEntries =
    canManageJournalEntries && (journalEntries.length > 0 || journalDraftEntry !== null)

  // We have an action that is triggered in this level, but the consequences of this action need a form
  // context, so the actual implementation happens in a nested component. We are following a register pattern
  const [onDropHandler, setOnDropHandler] = useState<(files: File[]) => void>(() => () => {})

  return (
    <AnimatePresence>
      <BulkActionsProvider expensable={expensable}>
        <Sidepanel
          expensable={expensable}
          open={openSidepanel}
          onClose={handleClose}
          onCloseClicked={handleCloseClicked}
          canRestartApprovalFlow={canRestartApprovalFlow}
          showTitle={!anyLoading}
        >
          {anyLoading && <Loading expand />}
          {!anyLoading && expensable && (
            <FilesPreviewerProvider initialFiles={initialFiles}>
              {openFilePreviewer && (
                <FilesPreviewer
                  onClose={handleClose}
                  onDrop={onDropHandler}
                  showUploadButton={isEditable}
                />
              )}
              <ConfirmationModals expensable={expensable} payroll={payroll} />
              <Tabs
                currentId={currentTab}
                tabsAlign="flexStart"
                onTabClicked={setCurrentTab}
                hasAdditionalPadding
              >
                <Tabs.Tab id="detail" label={i18n.t('expenses_v2.modals.detail.tabs.summary')}>
                  {(policyFlagsLoading ||
                    allFlags.length > 0 ||
                    duplicateAlertsForPolicyFlags.length > 0 ||
                    perDiemConflictAlertsForPolicyFlags.length > 0) && (
                    <PolicyFlags
                      loading={policyFlagsLoading}
                      flags={allFlags}
                      relevantPolicies={aiRelevantPolicies}
                      duplicateAlerts={duplicateAlertsForPolicyFlags}
                      perDiemConflictAlerts={perDiemConflictAlertsForPolicyFlags}
                      expensable={expensable}
                      canOverrideApprovalFlow={canOverrideApprovalFlow}
                    />
                  )}
                  <Form
                    expensable={expensable}
                    taxAgencyOrganization={signingOrganization}
                    customizationFields={customizationFields}
                    adeTermsAccepted={adeTermsAccepted}
                    onClose={handleClose}
                    hasReportOnBehalfFeature={reportOnBehalfEnabled}
                    hasProjectsFeature={projectsManagementEnabled}
                    reimburseThroughPayrollEnabled={reimburseThroughPayrollEnabled}
                    reimburseThroughSepaEnabled={reimburseThroughSepaEnabled}
                    refetchExpensable={refetchExpensable}
                    refetchSpendingPolicyEvaluate={refetchSpendingPolicyEvaluate}
                    setSpendingPolicyEvaluationResult={setSpendingPolicyEvaluationResult}
                    canUseSpendingPolicies={
                      canUseSpendingPolicies && !expensesPolicySettingsEnabled
                    }
                    canCreateExpensesForOthers={canCreateExpensesForOthers}
                    canOverrideApprovalFlow={canOverrideApprovalFlow}
                    canSetExpensableToPaid={canSetExpensableToPaid}
                    canReviewExpensable={canReviewExpensable}
                    canSeeEmployeeLegalEntity={canSeeEmployeeLegalEntity}
                    canCustomizeSettings={canCustomizeSettings}
                    isEditable={isEditable}
                    canDeleteExpensable={canDeleteExpensable}
                    currencies={currencies}
                    onClickSnackBar={() => setCurrentTab('alerts')}
                    toggleAlert={toggleAlert}
                    hideAlert={hideAlert}
                    budget={budget}
                    budgetAllocation={budgetAllocation}
                    spendingPolicyEvaluationResult={spendingPolicyEvaluationResult}
                    spendingAlerts={spendingAlerts}
                    registerDropHandler={setOnDropHandler}
                    approvalsV2Enabled={approvalsV2Enabled}
                    approvalProcess={approvalProcess}
                    timelineEntries={timelineEntries}
                    hasExpensablesF0Forms={hasExpensablesF0Forms}
                  />
                </Tabs.Tab>
                <Tabs.Tab id="comments" label={i18n.t('expenses_v2.modals.detail.tabs.comments')}>
                  <Comments expensableId={expensable.id} />
                </Tabs.Tab>
                {hasVisibleAlertsForNewPolicy ? (
                  <Tabs.Tab
                    id="alerts"
                    label={
                      alertCount > 0
                        ? `${i18n.t('expenses_v2.modals.detail.tabs.alerts')} (${alertCount})`
                        : i18n.t('expenses_v2.modals.detail.tabs.alerts')
                    }
                  >
                    <Alerts
                      expensable={expensable}
                      budget={budget}
                      budgetAllocation={budgetAllocation}
                      policiesResultData={spendingPolicyEvaluationResult}
                      canSeeBudgets={canSeeBudgets}
                      canCreateBudgets={canCreateBudgets}
                      canCreateSpendingPolicies={canCreateSpendingPolicies}
                      spendingAlerts={spendingAlerts}
                      canOverrideApprovalFlow={canOverrideApprovalFlow}
                      expensesPolicySettingsEnabled={expensesPolicySettingsEnabled}
                    />
                  </Tabs.Tab>
                ) : (
                  <></>
                )}

                {accountingTabEnabled && canSeeJournalEntries ? (
                  <Tabs.Tab
                    id="accounting"
                    label={i18n.t('expenses_v2.modals.detail.tabs.accounting')}
                  >
                    <Accounting expensable={expensable} />
                  </Tabs.Tab>
                ) : (
                  <></>
                )}
              </Tabs>
            </FilesPreviewerProvider>
          )}
          {!anyLoading && !expensable && (
            <BlankSlate
              illustration="sign-post"
              title={i18n.t('errors.page_not_found.title')}
              description={i18n.t('errors.page_not_found.description')}
            />
          )}
        </Sidepanel>
      </BulkActionsProvider>
    </AnimatePresence>
  )
}

export default observer(ExpensableDetail)
